import styled from 'styled-components';
import { isColorDark } from './DashboardTeam.utils';

export const CardTitle = styled.h3`
  font-family: 'Poppins', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: left;
  margin-bottom: 10px;
`;

export const Chart = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  display: flex;
  justify-content: ${props => (props.$layoutMode === 'tree' ? 'flex-start' : 'center')};
  gap: 24px;
  align-items: flex-start;
  min-width: max-content;
  padding: 12px 48px 12px 12px;
`;

export const Container = styled.div`
  position: relative;
  width: 100%;
  min-height: 0;
  max-height: calc(100vh - 180px);
  overflow-x: auto;
  overflow-y: auto;
  padding: 0 16px 16px;
  cursor: auto;
`;

export const ChartPane = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
`;

export const ChartPaneContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

export const ScrollControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

export const CenterControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  min-width: 0;
`;

export const LayoutToggleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const LayoutToggleButton = styled.button<{ active?: boolean }>`
  appearance: none;
  border: 1px solid #cbd5e1;
  background: ${props => (props.active ? '#ffffff' : '#f8fafc')};
  color: #334155;
  border-radius: 10px;
  width: 38px;
  height: 38px;
  min-width: 38px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease;

  &:hover {
    background: ${props => (props.active ? '#f8fafc' : '#eef2ff')};
    border-color: #94a3b8;
  }

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

export const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

export const ScrollButton = styled.button`
  appearance: none;
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #334155;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background-color 120ms ease, border-color 120ms ease, transform 120ms ease, opacity 120ms ease;

  &:hover:not(:disabled) {
    background: #eef2ff;
    border-color: #94a3b8;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

export const ChartHeader = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 12px 0;
  background: #ffffff;
  z-index: 20;
  border-bottom: 1px solid rgba(17,24,39,0.08);
  margin-bottom: 12px;
`;

export const TitleBarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(17,24,39,0.08);
  margin-bottom: 4px;
`;

export const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FilterSelect = styled.select`
  min-width: 200px;
  height: 38px;
  padding: 0 36px 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23374151' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat right 12px center;
  background-size: 10px 6px;
  color: #111827;
  font-size: 13px;
  line-height: 1.2;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:focus-visible,
  &:focus {
    outline: none;
    border-color: #94a3b8;
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 220px;
`;

export const NodeBox = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  position: relative;
  z-index: 3;
  width: ${props => (props.$layoutMode === 'tree' ? '240px' : 'auto')};
  min-width: ${props => (props.$layoutMode === 'tree' ? '240px' : '200px')};
  max-width: ${props => (props.$layoutMode === 'tree' ? '240px' : '220px')};
  text-align: center;
  border-radius: 8px;
  background: white;
  border: 1px solid rgba(17,24,39,0.12);
  box-shadow: 0 2px 8px rgba(17,24,39,0.20);
  overflow: visible;
  cursor: pointer;
`;

export const TitleBar = styled.div<{ $color?: string | null }>`
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color || '#f3f4f6'};
  color: ${props => (props.$color ? (isColorDark(props.$color) ? '#ffffff' : '#0f172a') : '#0f172a')};
  font-weight: 700;
  border-radius: 6px 6px 0 0;
`;

export const TitleText = styled.div`
  padding: 4px 10px;
  font-size: 14px;
  line-height: 1;
`;

export const CollapseButton = styled.button`
  position: absolute;
  top: calc(100% + 1px);
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid rgba(17,24,39,0.22);
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  z-index: 6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  transition: background 120ms ease, transform 120ms ease, box-shadow 120ms ease;

  &:hover {
    background: #f3f4f6;
    transform: translateX(-50%) translateY(-1px);
    box-shadow: 0 2px 6px rgba(15,23,42,0.12);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.2);
  }
`;

export const CollapseIcon = styled.span<{ $hideOnDefault?: boolean }>`
  position: relative;
  left: 0;
  opacity: ${props => (props.$hideOnDefault ? 0 : 1)};
  transition: opacity 120ms ease;
  ${CollapseButton}:hover & {
    opacity: 1;
  }
`;

export const Meta = styled.div<{ $compactView?: boolean }>`
  color: #4b5563;
  font-size: 13px;
  font-weight: 550;
  margin-top: ${props => (props.$compactView ? '4px' : '8px')};
`;

export const NodeCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Empty = styled.div`
  color: #6b7280;
  padding: 20px;
  text-align: center;
`;

export const NodeWrapper = styled.div<{ $layoutMode?: 'horizontal' | 'tree' }>`
  display: flex;
  flex-direction: column;
  align-items: ${props => (props.$layoutMode === 'tree' ? 'flex-start' : 'center')};
  position: relative;
  padding-bottom: 8px;
`;

export const TreeChildrenColumn = styled.div<{ $compactView?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${props => (props.$compactView ? '22px' : '16px')};
  margin-top: ${props => (props.$compactView ? '24px' : '20px')};
  margin-left: 120px;
  position: relative;
  padding-left: 20px;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #e6e6e6;
    z-index: 0;
  }
`;

export const TreeChildItem = styled.div`
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: -20px;
    top: 18px;
    width: 20px;
    height: 2px;
    background: #e6e6e6;
    z-index: 0;
  }
`;

export const TreeParentConnector = styled.div<{ $compactView?: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: calc(50% - 1px);
  width: 2px;
  height: ${props => (props.$compactView ? '20px' : '16px')};
  background: #e6e6e6;
  z-index: 0;
`;

export const MetricsRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
`;

export const MetricBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #111827;
  padding-left: 8px;
  white-space: nowrap;
  flex-wrap: nowrap;
  overflow: visible;
`;

export const MetricDot = styled.span<{ $bg: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.$bg};
  box-shadow: 0 1px 2px rgba(0,0,0,0.08);
`;

export const DoughnutBubble = styled.div`
  position: absolute;
  top: -16px;
  right: -22px;
  z-index: 20;
  pointer-events: none;
`;

export const DoughnutWrapper = styled.div<{ size?: number }>`
  width: ${p => (p.size ? `${p.size}px` : '46px')};
  height: ${p => (p.size ? `${p.size}px` : '46px')};
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  padding: 2px;
  border-radius: 999px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  box-sizing: content-box;
`;

export const ChildrenRow = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 28px;
  position: relative;
  align-items: flex-start;
`;

export const HorizontalLine = styled.div`
  position: absolute;
  top: -18px;
  left: 0;
  right: 0;
  height: 2px;
  background: #e6e6e6;
  z-index: 1;
`;

export const VerticalConnector = styled.div`
  position: absolute;
  top: 46px;
  left: calc(50% - 1px);
  width: 2px;
  height: 16px;
  background: #e6e6e6;
  z-index: 1;
`;

export const ModalContent = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
  gap: 16px;
  align-items: start;
`;

export const OverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  width: 100%;
  max-width: 360px;
  margin-top: 8px;

  & > * {
    min-width: 0;
    width: 100%;
  }
`;

export const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(260px, 1fr));
  grid-auto-rows: minmax(0, auto);
  gap: 16px;
  justify-items: stretch;
  align-items: start;
  font-family: 'Poppins', sans-serif;
  margin-top: 8px;

  & > div {
    min-width: 0;
    width: 100%;
  }

  & .pillar-card {
    min-width: 0 !important;
    width: 100% !important;
  }
`;

export const IndependentSection = styled.div`
  margin-top: 8px;
  margin-right: 24px;
  margin-left: 24px;
  padding-top: 10px;
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: flex-start;
`;

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;

export const StyledCard = styled.div`
  flex-grow: 1;
  border-radius: 12px;
  padding: 20px;
  box-shadow: none;
  border: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 585px;
`;
